/**
 * Folder-as-pack model: each pack is a Drive folder containing pack.dpack.
 * Pack id = folder id. The .dpack file holds pack name + playlist (slides/items).
 */
import { mergeDeckSlideShortcutsPreferLocal } from './slideShortcuts';

const DPACK_FILENAME = "pack.dpack";

/** Create a folder and put pack.dpack inside it. Returns { id: folderId, name }. Optional parentFolderId for My Drive or Shared drive parents. */
export const createPackFile = async (accessToken, packName, parentFolderId) => {
  const folderMeta = {
    name: packName,
    mimeType: "application/vnd.google-apps.folder",
  };
  if (parentFolderId) {
    folderMeta.parents = [parentFolderId];
  }

  const folderRes = await fetch(
    "https://www.googleapis.com/drive/v3/files?supportsAllDrives=true",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(folderMeta),
    }
  );
  if (!folderRes.ok) {
    const err = await folderRes.text();
    throw new Error(`Failed to create pack folder: ${err}`);
  }
  const folder = await folderRes.json();
  const folderId = folder.id;

  const metadata = {
    name: DPACK_FILENAME,
    mimeType: "application/json",
    parents: [folderId],
  };
  const content = JSON.stringify({
    packName,
    slides: [],
  });
  const form = new FormData();
  form.append(
    "metadata",
    new Blob([JSON.stringify(metadata)], { type: "application/json" })
  );
  form.append("file", new Blob([content], { type: "application/json" }));

  const fileRes = await fetch(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    }
  );
  if (!fileRes.ok) {
    const err = await fileRes.text();
    throw new Error(`Failed to create pack.dpack: ${err}`);
  }

  return { id: folderId, name: packName };
};

/** List packs = folders that contain pack.dpack. Returns [{ id, name }] where id is folder id. */
export const listPackFiles = async (accessToken) => {
  try {
    const listRes = await fetch(
      "https://www.googleapis.com/drive/v3/files?" +
        new URLSearchParams({
          q: `name='${DPACK_FILENAME}' and mimeType='application/json' and trashed=false`,
          fields: "files(id,parents)",
          supportsAllDrives: "true",
          includeItemsFromAllDrives: "true",
        }),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      }
    );
    if (!listRes.ok) {
      // If the token is no longer valid, flag auth expiry so the app can reset.
      if (listRes.status === 401 || listRes.status === 403) {
        localStorage.setItem('deckpack_auth_expired', Date.now().toString());
        return [];
      }
      throw new Error(`HTTP error! status: ${listRes.status}`);
    }
    const data = await listRes.json();
    const files = data.files || [];
    const folderIds = [...new Set(files.map((f) => (f.parents || [])[0]).filter(Boolean))];
    if (folderIds.length === 0) return [];

    const names = await Promise.all(
      folderIds.map(async (id) => {
        const r = await fetch(
          `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?fields=name&supportsAllDrives=true`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!r.ok) return null;
        const o = await r.json();
        return { id, name: o.name || "" };
      })
    );
    return names.filter((n) => n && n.name);
  } catch (error) {
    console.error("Error listing pack files:", error);
    return [];
  }
};

/** Return the file id of pack.dpack inside the given folder (packId = folder id). */
async function getDpackFileIdInFolder(accessToken, folderId) {
  const res = await fetch(
    "https://www.googleapis.com/drive/v3/files?" +
      new URLSearchParams({
        q: `'${folderId}' in parents and name='${DPACK_FILENAME}' and trashed=false`,
        fields: "files(id)",
        supportsAllDrives: "true",
        includeItemsFromAllDrives: "true",
      }),
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
  if (!res.ok) return null;
  const data = await res.json();
  const file = (data.files || [])[0];
  return file ? file.id : null;
}

/** List all folders in the user's Drive (flat list for now). */
export const listAllFolders = async (accessToken) => {
  try {
    const res = await fetch(
      "https://www.googleapis.com/drive/v3/files?" +
        new URLSearchParams({
          q: "mimeType='application/vnd.google-apps.folder' and trashed=false and (sharedWithMe or 'me' in owners)",
          fields: "files(id,name,parents)",
          includeItemsFromAllDrives: "true",
          supportsAllDrives: "true",
        }),
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    if (!res.ok) throw new Error(`Error listing folders: ${res.status}`);
    const data = await res.json();
    return data.files || [];
  } catch (err) {
    console.error("Error listing folders:", err);
    return [];
  }
};

/** Create pack.dpack inside an existing folder if it doesn't already exist. */
export const ensurePackForFolder = async (accessToken, folderId, folderName) => {
  // If pack.dpack already exists, just treat this folder as a pack
  const existingId = await getDpackFileIdInFolder(accessToken, folderId);
  if (!existingId) {
    const metadata = {
      name: DPACK_FILENAME,
      mimeType: "application/json",
      parents: [folderId],
    };
    const content = JSON.stringify({
      packName: folderName || "",
      slides: [],
    });
    const form = new FormData();
    form.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" })
    );
    form.append("file", new Blob([content], { type: "application/json" }));

    const fileRes = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true",
      {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      }
    );

    if (!fileRes.ok) {
      const err = await fileRes.text();
      throw new Error(`Failed to create pack.dpack in folder: ${err}`);
    }
  }

  // Folder itself is the pack id
  return { id: folderId, name: folderName || "" };
};

/** Load pack: packId = folder id. Reads pack.dpack from that folder. */
export const loadPack = async (accessToken, packId) => {
  try {
    const fileId = await getDpackFileIdInFolder(accessToken, packId);
    if (!fileId) return { slides: [], removedFileIds: [], deckSlideShortcuts: {} };

    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!res.ok) return { slides: [], removedFileIds: [], deckSlideShortcuts: {} };

    const packData = await res.json();
    return {
      ...packData,
      slides: Array.isArray(packData.slides) ? packData.slides : [],
      removedFileIds: Array.isArray(packData.removedFileIds) ? packData.removedFileIds : [],
      deckSlideShortcuts:
        packData.deckSlideShortcuts && typeof packData.deckSlideShortcuts === 'object'
          ? packData.deckSlideShortcuts
          : {},
    };
  } catch (error) {
    console.error("Error loading pack file:", error);
    return { slides: [], removedFileIds: [], deckSlideShortcuts: {} };
  }
};

/**
 * Save playlist to pack. packId = folder id. payload = { slides, removedFileIds? }.
 * @returns {{ ok: boolean, status?: number, message?: string, step?: string }}
 */
export const savePackWithMeta = async (accessToken, packId, payload) => {
  try {
    const fileId = await getDpackFileIdInFolder(accessToken, packId);
    if (!fileId) {
      return { ok: false, step: "no-dpack", message: "No pack.dpack file found in this folder." };
    }

    const slides = Array.isArray(payload) ? payload : payload.slides;
    const removedFileIds = Array.isArray(payload) ? [] : (payload.removedFileIds || []);
    const payloadObj = Array.isArray(payload) ? {} : payload;
    let deckSlideShortcuts = payloadObj.deckSlideShortcuts;
    if (deckSlideShortcuts === undefined) {
      const existing = await loadPack(accessToken, packId);
      deckSlideShortcuts =
        existing.deckSlideShortcuts && typeof existing.deckSlideShortcuts === 'object'
          ? existing.deckSlideShortcuts
          : {};
    } else if (!deckSlideShortcuts || typeof deckSlideShortcuts !== 'object') {
      deckSlideShortcuts = {};
    }
    const content = JSON.stringify({ slides, removedFileIds, deckSlideShortcuts });
    const url = `https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(
      fileId
    )}?uploadType=media&supportsAllDrives=true`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: content,
    });
    if (!res.ok) {
      const errText = (await res.text().catch(() => "")).slice(0, 800);
      console.error("savePack failed:", res.status, errText);
      return { ok: false, status: res.status, message: errText || res.statusText, step: "upload" };
    }
    return { ok: true };
  } catch (error) {
    console.error("Error saving pack file:", error);
    return { ok: false, step: "exception", message: String(error?.message || error) };
  }
};

/** Save playlist to pack. Returns true on success. */
export const savePack = async (accessToken, packId, payload) => {
  const r = await savePackWithMeta(accessToken, packId, payload);
  return r.ok;
};

/** Rename pack = rename folder. packId = folder id. */
export const updatePackName = async (accessToken, packId, newName) => {
  const url = new URL(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(packId)}`);
  url.searchParams.set("supportsAllDrives", "true");
  const res = await fetch(url.toString(), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name: newName }),
  });
  return res.ok;
};

/** Remove pack.dpack from a folder without deleting the folder itself. */
export const unsetPackForFolder = async (accessToken, folderId) => {
  try {
    const fileId = await getDpackFileIdInFolder(accessToken, folderId);
    if (!fileId) {
      // No pack.dpack file exists, so it's already "unset" - return true
      return true;
    }
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?supportsAllDrives=true`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    
    if (!res.ok) {
      const errorText = await res.text().catch(() => 'Unknown error');
      console.error(`Failed to delete pack.dpack file: ${res.status} - ${errorText}`);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error("Error unsetting pack for folder:", err);
    return false;
  }
};

/** Returns true if the file is already located in the given folder (by parent id). */
export const fileIsInFolder = async (accessToken, fileId, folderId) => {
  try {
    const getRes = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents&supportsAllDrives=true`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!getRes.ok) return false;
    const file = await getRes.json();
    return (file.parents || []).includes(folderId);
  } catch (err) {
    console.error('fileIsInFolder:', err);
    return false;
  }
};

/** Move a file into a folder (e.g. pack folder). Gets current parents, then updates. */
export const moveFileIntoFolder = async (accessToken, fileId, toFolderId) => {
  try {
    const getRes = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents&supportsAllDrives=true`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!getRes.ok) {
      const errText = await getRes.text().catch(() => '');
      console.error(`moveFileIntoFolder get failed: ${getRes.status}`, errText);
      return false;
    }
    const file = await getRes.json();
    const parents = file.parents || [];
    if (parents.length === 0) return false;
    if (parents.includes(toFolderId)) return true; // already in target folder
    const removeParents = parents.join(',');

    const url = new URL(`https://www.googleapis.com/drive/v3/files/${fileId}`);
    url.searchParams.set('addParents', toFolderId);
    url.searchParams.set('removeParents', removeParents);
    url.searchParams.set('supportsAllDrives', 'true');

    const res = await fetch(url.toString(), {
      method: "PATCH",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error(`moveFileIntoFolder failed: ${res.status}`, errText);
      return false;
    }
    return true;
  } catch (err) {
    console.error("Error moving file into folder:", err);
    return false;
  }
};

/** Move a file out of a pack folder to a different parent (e.g. My Drive).
 *  Uses Drive API files.update with addParents and removeParents.
 *  'root' is the alias for the user's My Drive root. */
export const moveFileToFolder = async (accessToken, fileId, fromFolderId, toFolderId) => {
  try {
    const url = new URL(`https://www.googleapis.com/drive/v3/files/${fileId}`);
    url.searchParams.set('addParents', toFolderId);
    url.searchParams.set('removeParents', fromFolderId);
    url.searchParams.set('supportsAllDrives', 'true');

    const res = await fetch(url.toString(), {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      // No body for metadata-only update; addParents/removeParents are query params
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error(`moveFileToFolder failed: ${res.status}`, errText);
      return false;
    }
    return true;
  } catch (err) {
    console.error("Error moving file:", err);
    return false;
  }
};

/** Sync manifest slides with actual files in the pack folder. */
export const syncPackWithFolder = async (accessToken, packId) => {
  try {
    // 1) List files currently in the folder (exclude subfolders)
    const filesRes = await fetch(
      "https://www.googleapis.com/drive/v3/files?" +
        new URLSearchParams({
          q: `'${packId}' in parents and trashed=false and mimeType!='application/vnd.google-apps.folder'`,
          fields: "files(id,name,mimeType)",
          supportsAllDrives: "true",
          includeItemsFromAllDrives: "true",
        }),
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    if (!filesRes.ok) {
      console.error("Failed to list pack folder children", await filesRes.text());
      return { slides: [], removedFileIds: [], deckSlideShortcuts: {} };
    }
    const filesData = await filesRes.json();
    let children = filesData.files || [];
    // Exclude the manifest file itself from the playlist
    children = children.filter((f) => f.name !== DPACK_FILENAME);
    const childrenById = new Map(children.map((f) => [f.id, f]));

    // 2) Load current manifest
    const packData = await loadPack(accessToken, packId);
    const existing = Array.isArray(packData.slides) ? packData.slides : [];
    let removedFileIds = packData.removedFileIds || [];

    const nextSlides = [];
    const seenOriginalFolderIds = new Set();

    // 3) Keep existing entries:
    // - All non-folder items (no source or source!=='folder') are preserved as-is.
    // - Folder items (source==='folder') are kept only if their file still exists.
    // - For legacy duplicates (same file id, no duplicateOf flag), keep only the first.
    for (const item of existing) {
      if (item.source === "folder") {
        const file = childrenById.get(item.id);
        if (!file) continue; // file was removed/moved; drop from playlist

        if (!item.duplicateOf) {
          // Legacy originals: avoid keeping multiple copies for the same file id
          if (seenOriginalFolderIds.has(item.id)) {
            continue;
          }
          seenOriginalFolderIds.add(item.id);
        }

        nextSlides.push({
          ...item,
          name: file.name,
          mimeType: file.mimeType,
          source: "folder",
        });
      } else {
        // external/legacy items (e.g. Google Slides not in this folder)
        nextSlides.push(item);
      }
    }

    // 4) Append new folder files that don't yet have a folder-sourced entry
    //    and are NOT in removedFileIds (user explicitly removed them from playlist)
    const hasFolderItemFor = (fileId) =>
      nextSlides.some((s) => s.source === "folder" && s.id === fileId);
    const removedSet = new Set(removedFileIds);

    let counter = 0;
    for (const file of children) {
      if (hasFolderItemFor(file.id)) continue;
      if (removedSet.has(file.id)) continue; // do not re-add files user removed from playlist
      nextSlides.push({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        source: "folder",
        playlistPositionId: Date.now() + counter++,
      });
    }

    // Clean removedFileIds: remove ids that are no longer in the folder (user moved/deleted them)
    removedFileIds = removedFileIds.filter((id) => childrenById.has(id));

    // 5) Re-read manifest immediately before write so concurrent shortcut / playlist saves are less likely to be lost.
    const latestPack = await loadPack(accessToken, packId);
    // Prefer the second read on key collisions; keep entries only present on the first read so a narrow race
    // between reads does not drop shortcuts.
    const deckSlideShortcuts = mergeDeckSlideShortcutsPreferLocal(
      latestPack.deckSlideShortcuts,
      packData.deckSlideShortcuts
    );

    await savePack(accessToken, packId, { slides: nextSlides, removedFileIds, deckSlideShortcuts });
    return { slides: nextSlides, removedFileIds, deckSlideShortcuts };
  } catch (err) {
    console.error("Error syncing pack with folder:", err);
    return { slides: [], removedFileIds: [], deckSlideShortcuts: {} };
  }
};
